import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useAppContext } from '../../../../redux/slices/appContext'
import { useAccountSearch } from '../../../../api/coreData'
import SydInput from '../../../commonDesign/SydInput'
import useDebouncedValue from '../../../../hooks/useDebounceValue'
import Skeleton from '../../../atoms/Skeleton'
import { ICON_NAMES } from '../../../../constants'
import RoundedRectangleNode from '../Canvas/nodes/RoundedRectangleNode'
import SydLabel from '../../../commonDesign/SydLabel'
import { useEntityGraphContext } from '../Wrapper/EntityGraphProvider'
import { getRandomId } from './utils'

const getAccountData = (account) => ({
  header: account.accountName,
  description: account.accountNumber,
  nodeProps: {
    size: 'sm'
  },
  entityProps: {
    levelTypeId: 1,
    levelId: account.accountId,
    type: 'account'
  },
  displayOptions: {
    header: 'entityProps.label',
    description: 'entityProps.description'
  }
})

export const AccountsList = ({
  actionType = 'creation',
  onSelectItem: _onSelectItem,
  selectedValue
}) => {
  const { clientId } = useAppContext()
  const { dispatch } = useEntityGraphContext()
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300)

  const addLinkedDataToContext = useCallback((account) => {
    dispatch({
      type: 'ADD_LINKED_ENTITY_DATA',
      payload: {
        levelTypeId: 1,
        levelId: account.accountId,
        value: {
          label: account.accountName,
          description: account.accountNumber,
          ...account
        }
      }
    })
  }, [dispatch])

  const onSelectItem = useCallback((account) => {
    addLinkedDataToContext(account)
    _onSelectItem?.(getAccountData(account))
  }, [addLinkedDataToContext, _onSelectItem])

  const accountQuery = useMemo(() => ({
    filters: {
      assignedToClientIds: [clientId],
      ...(selectedValue && {
        accountId: [{ op: 'eq', value: selectedValue.levelId }]
      })
    },
    take: 10,
    ...(debouncedSearchTerm?.length && {
      textSearch: {
        accountName: debouncedSearchTerm,
        accountNumber: debouncedSearchTerm
      }
    })
  }), [clientId, debouncedSearchTerm, selectedValue])
  const { data, isLoading } = useAccountSearch(accountQuery)

  const onDragStart = (event, account) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify({
      nodeType: 'roundedRectangle',
      id: `account_${account.accountId}_${getRandomId()}`,
      data: getAccountData(account)
    }))
    event.dataTransfer.effectAllowed = 'move'
    addLinkedDataToContext(account)
  }

  if (selectedValue) {
    return (
      <>
        {isLoading && (
          <Skeleton width='100%' height='38px' />
        )}
        {data?.data?.map((account) => (
          <RoundedRectangleNode
            key={account.accountId}
            width='100%'
            height='auto'
            data={{
              ...getAccountData(account),
              nodeProps: {
                size: 'md',
                handles: [],
                targets: []
              }
            }}
          />
        ))}
      </>
    )
  }

  return (
    <>
      <div>
        <SydInput
          size='sm'
          iconName={ICON_NAMES.search}
          placeholder='Search accounts...'
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          className='mb-2'
        />
      </div>
      <div className='flex flex-col gap-sm'>
        <SydLabel label={actionType === 'linking' ? 'Click an account to link it' : 'Drag account into the chart'} />
        {isLoading && Array.from({ length: 10 }).map((_, index) => (
          <Skeleton width='100%' height='38px' key={index} />
        ))}
        {data?.data?.map((account) => (
          <div
            draggable={actionType === 'creation'}
            key={account.accountId}
            className='relative'
            style={{
              cursor: actionType === 'creation' ? 'grab' : 'pointer'
            }}
            onDragStart={(e) => onDragStart(e, account)}
            onClick={() => {
              if (actionType === 'linking' && onSelectItem) {
                onSelectItem(account)
              }
            }}
          >
            <RoundedRectangleNode
              width='100%' height='auto' data={{
                ...getAccountData(account),
                nodeProps: {
                  size: 'md',
                  handles: [],
                  targets: []
                }
              }}
            />
          </div>
        ))}
      </div>
    </>
  )
}

AccountsList.propTypes = {
  actionType: PropTypes.oneOf(['creation', 'linking']),
  onSelectItem: PropTypes.func,
  selectedValue: PropTypes.bool
}
